import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain, ArrowLeft, MapPin, Calendar, Clock, Thermometer, Drop, Wind, Warning, ArrowsLeftRight } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const CompareWeather = () => {
  const { user } = useAuth();
  const [formA, setFormA] = useState({ location: '', date: '', time: '' });
  const [formB, setFormB] = useState({ location: '', date: '', time: '' });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResults(null);

    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/weather/compare`,
        {
          location_a: formA.location, date_a: formA.date, time_a: formA.time,
          location_b: formB.location, date_b: formB.date, time_b: formB.time,
        },
        { withCredentials: true }
      );
      setResults(data);
      toast.success('Comparison data retrieved successfully');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'ERROR: Unable to compare weather data. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const WeatherCard = ({ label, data: res }) => {
    if (!res) return null;
    if (res.error) {
      return (
        <div className="bg-red-50 border border-red-200 p-6">
          <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-red-500 mb-2">{label}</h3>
          <p className="text-sm text-red-700">{res.error}</p>
        </div>
      );
    }

    const wd = res.weather_data?.weather_data || {};
    return (
      <div className="bg-white border border-slate-200 p-6 flex-1">
        <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">{label}</h3>
        <p className="text-sm font-medium text-slate-950 mb-1">{res.location?.formatted_address || res.input?.location}</p>
        <p className="text-xs font-mono text-slate-500 mb-4">{res.input?.date} {res.input?.time}</p>

        <div className="grid grid-cols-2 gap-3">
          <div className="border border-slate-200 p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Thermometer size={16} weight="bold" className="text-blue-600" />
              <p className="text-xs text-slate-500 uppercase tracking-wider">Temp</p>
            </div>
            <p className="text-2xl font-mono font-bold text-slate-950">{wd.temp ?? 'N/A'}<span className="text-sm text-slate-500">°F</span></p>
          </div>
          <div className="border border-slate-200 p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Drop size={16} weight="bold" className="text-blue-600" />
              <p className="text-xs text-slate-500 uppercase tracking-wider">Humidity</p>
            </div>
            <p className="text-2xl font-mono font-bold text-slate-950">{wd.humidity ?? 'N/A'}<span className="text-sm text-slate-500">%</span></p>
          </div>
          <div className="border border-slate-200 p-4">
            <div className="flex items-center gap-1.5 mb-2">
              <Wind size={16} weight="bold" className="text-blue-600" />
              <p className="text-xs text-slate-500 uppercase tracking-wider">Wind</p>
            </div>
            <p className="text-2xl font-mono font-bold text-slate-950">{wd.windspeed ?? 'N/A'}<span className="text-sm text-slate-500">mph</span></p>
          </div>
          <div className="border border-slate-200 p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Conditions</p>
            <p className="text-sm font-medium text-slate-950">{wd.conditions || 'N/A'}</p>
          </div>
        </div>

        {res.weather_data?.warning && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200">
            <p className="text-xs text-amber-700"><Warning size={12} className="inline mr-1" />{res.weather_data.warning}</p>
          </div>
        )}
      </div>
    );
  };

  const DiffRow = ({ label, valA, valB, unit }) => {
    const a = parseFloat(valA);
    const b = parseFloat(valB);
    const diff = !isNaN(a) && !isNaN(b) ? (b - a).toFixed(1) : null;
    const diffColor = diff === null ? 'text-slate-500' : parseFloat(diff) > 0 ? 'text-red-600' : parseFloat(diff) < 0 ? 'text-blue-600' : 'text-slate-500';

    return (
      <div className="grid grid-cols-4 gap-4 py-2 border-b border-slate-100 text-sm">
        <p className="text-slate-500 font-medium">{label}</p>
        <p className="font-mono text-slate-950 text-right">{valA ?? 'N/A'}{unit}</p>
        <p className="font-mono text-slate-950 text-right">{valB ?? 'N/A'}{unit}</p>
        <p className={`font-mono text-right font-semibold ${diffColor}`}>
          {diff !== null ? `${parseFloat(diff) > 0 ? '+' : ''}${diff}${unit}` : '—'}
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white mobile-nav-padding">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <CloudRain size={28} weight="bold" className="text-blue-600" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
            <Link to="/dashboard" data-testid="back-to-dashboard-link" className="text-sm text-slate-600 hover:text-slate-950 transition-colors flex items-center gap-1.5">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tighter text-slate-950" style={{ fontFamily: 'Chivo' }}>Compare Weather</h1>
          <p className="text-sm text-slate-600 mt-2">Compare historical weather between two locations or dates side by side</p>
        </div>

        <form onSubmit={handleSubmit} className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Location A */}
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Location A</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2"><MapPin size={14} className="inline mr-1" />Location</label>
                  <input data-testid="compare-location-a" type="text" value={formA.location} onChange={(e) => setFormA({ ...formA, location: e.target.value })} required className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm" placeholder="City, ZIP, or address" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2"><Calendar size={14} className="inline mr-1" />Date</label>
                    <input data-testid="compare-date-a" type="date" value={formA.date} onChange={(e) => setFormA({ ...formA, date: e.target.value })} required max={new Date().toISOString().split('T')[0]} className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2"><Clock size={14} className="inline mr-1" />Time</label>
                    <input data-testid="compare-time-a" type="time" value={formA.time} onChange={(e) => setFormA({ ...formA, time: e.target.value })} required className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono" />
                  </div>
                </div>
              </div>
            </div>

            {/* Location B */}
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Location B</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2"><MapPin size={14} className="inline mr-1" />Location</label>
                  <input data-testid="compare-location-b" type="text" value={formB.location} onChange={(e) => setFormB({ ...formB, location: e.target.value })} required className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm" placeholder="City, ZIP, or address" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2"><Calendar size={14} className="inline mr-1" />Date</label>
                    <input data-testid="compare-date-b" type="date" value={formB.date} onChange={(e) => setFormB({ ...formB, date: e.target.value })} required max={new Date().toISOString().split('T')[0]} className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-2"><Clock size={14} className="inline mr-1" />Time</label>
                    <input data-testid="compare-time-b" type="time" value={formB.time} onChange={(e) => setFormB({ ...formB, time: e.target.value })} required className="w-full px-4 py-2 border border-slate-300 focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:outline-none text-sm font-mono" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button data-testid="compare-submit-btn" type="submit" disabled={loading} className="w-full mt-6 px-6 py-3 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center gap-2">
            <ArrowsLeftRight size={20} weight="bold" />
            {loading ? 'Comparing...' : 'Compare Weather Data'}
          </button>
        </form>

        {/* Results */}
        {results && (
          <div data-testid="compare-results" className="space-y-6">
            <div className="flex gap-6">
              <WeatherCard label="Location A" data={results.result_a} />
              <WeatherCard label="Location B" data={results.result_b} />
            </div>

            {/* Diff table */}
            {!results.result_a?.error && !results.result_b?.error && (
              <div className="bg-slate-50 border border-slate-200 p-6">
                <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500 mb-4">Difference Summary</h3>
                <div className="grid grid-cols-4 gap-4 py-2 border-b border-slate-300 text-xs font-medium uppercase tracking-wider text-slate-500">
                  <p>Metric</p>
                  <p className="text-right">Location A</p>
                  <p className="text-right">Location B</p>
                  <p className="text-right">Difference</p>
                </div>
                <DiffRow label="Temperature" valA={results.result_a?.weather_data?.weather_data?.temp} valB={results.result_b?.weather_data?.weather_data?.temp} unit="°F" />
                <DiffRow label="Humidity" valA={results.result_a?.weather_data?.weather_data?.humidity} valB={results.result_b?.weather_data?.weather_data?.humidity} unit="%" />
                <DiffRow label="Wind Speed" valA={results.result_a?.weather_data?.weather_data?.windspeed} valB={results.result_b?.weather_data?.weather_data?.windspeed} unit="mph" />
                <DiffRow label="Pressure" valA={results.result_a?.weather_data?.weather_data?.pressure} valB={results.result_b?.weather_data?.weather_data?.pressure} unit="mb" />
                <DiffRow label="Cloud Cover" valA={results.result_a?.weather_data?.weather_data?.cloudcover} valB={results.result_b?.weather_data?.weather_data?.cloudcover} unit="%" />
              </div>
            )}
          </div>
        )}

        <div className="mt-8 p-4 border border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-600 leading-relaxed">
            <strong className="text-slate-900">DISCLAIMER:</strong> This comparison is generated using historical weather data from third-party data providers. While data is sourced from reputable services, accuracy is not guaranteed.
          </p>
        </div>
      </main>
    </div>
  );
};

export default CompareWeather;
